// packages/shared-components/src/components/Modal/Modal.tsx
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";
import { Iconfont } from "../Iconfont";
import { ModalProps } from "./types";
import { renderModal, destroyModal, ModalFuncProps } from "./modal-render";
import "./Modal.css";

export const Modal = ({
  visible,
  title,
  closable = true,
  footer = true,
  okText,
  cancelText,
  onOk,
  onCancel,
  onClose,
  className,
  width = 520,
  children,
}: ModalProps) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (visible) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
      document.body.style.overflow = "hidden";
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        document.body.style.overflow = "";
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [visible, isVisible]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const [modalStyle, setModalStyle] = useState<React.CSSProperties>({});
  const [dragInfo, setDragInfo] = useState({
    startX: 0,
    startY: 0,
    left: 0,
    top: 0,
  });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!(e.target as HTMLElement).closest(".shared-modal-header")) return;
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    setDragInfo({
      startX: e.clientX,
      startY: e.clientY,
      left: rect.left,
      top: rect.top,
    });
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const startDrag = (e: MouseEvent) => {
      if (!dragInfo.startX) return;
      const left = dragInfo.left + (e.clientX - dragInfo.startX);
      const top = dragInfo.top + (e.clientY - dragInfo.startY);
      setModalStyle({
        position: "fixed",
        left: 0,
        top: 0,
        transform: `translate(${left}px, ${top}px)`,
        margin: 0,
      });
    };

    const stopDrag = () => {
      document.body.style.userSelect = "";
      setDragInfo({ startX: 0, startY: 0, left: 0, top: 0 });
    };

    document.addEventListener("mousemove", startDrag);
    document.addEventListener("mouseup", stopDrag);
    return () => {
      document.removeEventListener("mousemove", startDrag);
      document.removeEventListener("mouseup", stopDrag);
    };
  }, [dragInfo]);

  const handleClose = () => {
    onClose?.();
    onCancel?.();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const handleOk = () => {
    onOk?.();
  };

  const handleMaskClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const modalContent = (
    <div
      className={`shared-modal-mask ${
        isAnimating ? "shared-modal-mask-enter" : "shared-modal-mask-exit"
      }`}
      onClick={handleMaskClick}
    >
      <div
        className={`shared-modal-container ${
          isAnimating
            ? "shared-modal-container-enter"
            : "shared-modal-container-exit"
        } ${className || ""}`}
        style={{
          width,
          ...modalStyle,
        }}
        onMouseDown={handleMouseDown}
      >
        {(title || closable) && (
          <div className="shared-modal-header">
            {title && <h3 className="shared-modal-title">{title}</h3>}
            {closable && (
              <button
                type="button"
                className="shared-modal-close"
                onClick={handleClose}
                aria-label={t("common.close")}
              >
                <Iconfont icon="close" className="shared-modal-close-icon" />
              </button>
            )}
          </div>
        )}

        <div className="shared-modal-body">
          {typeof children === "string" ? (
            <div dangerouslySetInnerHTML={{ __html: children }} />
          ) : (
            children
          )}
        </div>

        {footer && (
          <div className="shared-modal-footer">
            {cancelText !== "" && (
              <button
                className="shared-modal-btn-cancel"
                onClick={handleCancel}
              >
                {cancelText || t("common.cancel")}
              </button>
            )}
            <button className="shared-modal-btn-ok" onClick={handleOk}>
              {okText || t("common.confirm")}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (!isVisible) return null;

  return createPortal(modalContent, getSubAppPortalContainer());
};

// 把带方法的对象做具名导出
export const ModalFunc = Object.assign(Modal, {
  confirm: (props: ModalFuncProps) => renderModal({ footer: true, ...props }),
  info: (props: ModalFuncProps) =>
    renderModal({ footer: true, cancelText: "", ...props }),
  success: (props: ModalFuncProps) =>
    renderModal({ footer: true, cancelText: "", ...props }),
  error: (props: ModalFuncProps) =>
    renderModal({ footer: true, cancelText: "", ...props }),
  content: (props: ModalFuncProps) =>
    renderModal({ footer: false, closable: true, ...props }),
  destroy: destroyModal,
});

/**
 * 调用方式
 * ModalFunc.confirm({
    title: "提示",
    // 可以传纯字符器，也支持html传入，但需要用反引号包裹 HTML 字符串，不要写 JSX 标签
    content: `<div style="color: red;">确定要执行此操作吗？</div>`,
    onOk: () => console.log("确定"),
  });
 */
